import { inject } from 'aurelia-framework';
import { HttpClient, json } from 'aurelia-fetch-client';

@inject(HttpClient)
export class QuoteService {
  constructor(http) {
    this.http = http;
    this.singular = 'quote';
    this.plural = 'quotes';
    this.add = PLATFORM.moduleName('pages/quotes/add/index');
    this.linkChildQuoteEditor = PLATFORM.moduleName('pages/quotes/child-quote-lookup/index');
    this.quoteProcessEditor = PLATFORM.moduleName('pages/quotes/quote-processes/editor/index');
    this.quoteCustomerProjectEditor = PLATFORM.moduleName('pages/standard-setup/customer-projects/editor/index');
  }

  async filter(loadOptions) {
    const response = await this.http.fetch(`quotes/filter/${loadOptions}`);
    return await response.json();
  }

  async printFilter(loadOptions, filter) {
    const response = await this.http.fetch(`quotes/print/filter/${loadOptions}`, { method: 'POST', body: json(filter) });
    return await response.json();
  }

  async print(quoteId) {
    const response = await this.http.fetch(`quotes/print/${quoteId}`);
    return await response.json();
  }

  async massPrint(filter) {
    const response = await this.http.fetch(`quotes/print/mass`, { method: 'POST', body: json({ quoteIds: filter }) });
    return await response.json();
  }

  async create(quote) {
    const response = await this.http.fetch('quotes', { method: 'POST', body: json(quote) });
    return await response.json();
  }

  async clone(quote) {
    const response = await this.http.fetch(`quotes/${quote.id}/clone`, { method: 'POST', body: json(quote) });
    return await response.json();
  }

  async update(quote) {
    await this.http.fetch(`quotes/${quote.id}`, { method: 'PUT', body: json(quote) });
  }

  async delete(id) {
    await this.http.fetch(`quotes/${id}`, { method: 'DELETE' });
  }

  async getById(id) {
    const response = await this.http.fetch(`quotes/${id}`);
    return await response.json();
  }

  async getAllowancesById(id) {
    const response = await this.http.fetch(`quotes/allowances/${id}`);
    return await response.json();
  }

  async getStampProcessPrices(standardId, processId, machineId) {
    const response = await this.http.fetch(`quotes/processes/stamp/prices/${standardId}/${processId}/${machineId}`);
    return await response.json();
  }
  
  async getWeldProcessPrices(standardId, processId, quoteId, partSizeId) {
    const response = await this.http.fetch(`quotes/processes/weld/prices/${standardId}/${processId}/${quoteId}/${partSizeId}`);
    return await response.json();
  }

  async getQuoteValidStatus(plantId, projectId, partId, customerId) {
    const response = await this.http.fetch(`quotes/valid-status/${plantId}/${projectId}/${partId}/${customerId}`);
    return await response.json();
  }

  async getGravitiesByCoatingId(coatingId) {
    const response = await this.http.fetch(`specific-gravities/coating/${coatingId}`);
    return await response.json();
  }

  async getCustomerProjectIdForQuote(plantId, projectId, customerId) {
    const response = await this.http.fetch(`quotes/customer-project/${plantId}/${projectId}/${customerId}`);
    return await response.json();
  }

  async getQuoteProcessIdsForQuote(quoteId) {
    const response = await this.http.fetch(`quotes/processes/ids/${quoteId}`);
    return await response.json();
  }

  async getQuotesByPart(partId) {
    const response = await this.http.fetch(`quotes/part/${partId}`);
    return await response.json();
  }

  async linkChildQuote(quoteId, structureId, childQuoteId) {
    await this.http.fetch(`quotes/${quoteId}/linkChild`, { method: 'POST', body: json({ structureId, childQuoteId })});
  }

  async getIdsByFilter(filter) {
    const response = await this.http.fetch(`quotes/ids-by-filter/${filter}`);
    return await response.json();
  }

  async unlock(quoteId) {
    await this.http.fetch(`quotes/${quoteId}/unlock`, { method: 'PUT' });
  }

  async filterSnapshots(loadOptions, params) {
    const response = await this.http.fetch(`quotes/${params[0]}/snapshots/filter/${JSON.stringify(params[1])}/${loadOptions}`);
    return await response.json();
  }

  async createSnapshot(quoteId, snapshot) {
    await this.http.fetch(`quotes/${quoteId}/snapshots`, { method: 'POST', body: json(snapshot) });
  }

  async updateSnapshot(quoteId, snapshot) {
    await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshot.id}`, { method: 'PUT', body: json(snapshot) });
  }

  async deleteSnapshot(quoteId, snapshotId) {
    await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshotId}`, { method: 'DELETE' });
  }

  async restoreSnapshot(quoteId, snapshotId) {
    await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshotId}/restore`, { method: 'POST' });
  }

  async getSnapshotData(quoteId, snapshotId) {
    const response = await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshotId}/quote`);
    return await response.json();
  }

  async getSnapshotById(quoteId, snapshotId) {
    const response = await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshotId}`);
    return await response.json();
  }

  async bulkSnapshot(snapshot) {
    await this.http.fetch(`quotes/snapshots/bulk`, { method: 'POST', body: json(snapshot) });
  }

  async getSnapshotByIdentifier(quoteId, identifier) {
    const response = await this.http.fetch(`quotes/${quoteId}/snapshots/identifier/${identifier}`);
    return await response.json();
  }

  async printSnapshot(quoteId, snapshotId) {
    const response = await this.http.fetch(`quotes/${quoteId}/snapshots/${snapshotId}/print`);
    return await response.json();
  }
}
